/**
 * Age Gate Session Management Utility
 * Handles browser-based age verification independent of user login
 */

const AGE_GATE_KEY = 'age_gate_verified';

class AgeGateManager {
    
    /**
     * Check if user has passed the age gate
     * @returns {boolean} True if verified and not expired
     */
    static isAgeGateVerified() {
        try {
            // Check sessionStorage first (current session)
            const sessionData = sessionStorage.getItem(AGE_GATE_KEY);
            if (sessionData) {
                const data = JSON.parse(sessionData);
                return this.isVerificationValid(data);
            }
            
            // Check localStorage (persistent across sessions)
            const localData = localStorage.getItem(AGE_GATE_KEY);
            if (localData) {
                const data = JSON.parse(localData);
                if (this.isVerificationValid(data)) {
                    // Copy to sessionStorage for faster access
                    sessionStorage.setItem(AGE_GATE_KEY, localData);
                    return true;
                }
            }
            
            return false;
        } catch (error) {
            console.error('Error checking age gate verification:', error);
            return false;
        }
    }
    
    /**
     * Check if verification data is valid and not expired
     * @param {Object} data - Verification data
     * @returns {boolean} True if valid
     */
    static isVerificationValid(data) {
        if (!data || !data.verified || !data.timestamp) {
            return false;
        }
        
        // Check if verification has expired
        if (data.expiry) {
            const now = new Date();
            const expiry = new Date(data.expiry);
            
            if (now > expiry) {
                // Verification expired, clean up
                this.clearAgeGate();
                return false;
            }
        }
        
        return true;
    }
    
    /**
     * Set age gate verification
     * @param {Object} verificationData - Verification data
     */
    static setAgeGateVerification(verificationData) {
        try {
            const dataString = JSON.stringify(verificationData);
            sessionStorage.setItem(AGE_GATE_KEY, dataString);
            localStorage.setItem(AGE_GATE_KEY, dataString);
        } catch (error) {
            console.error('Error setting age gate verification:', error);
        }
    }
    
    /**
     * Clear age gate verification
     */
    static clearAgeGate() {
        try {
            sessionStorage.removeItem(AGE_GATE_KEY);
            localStorage.removeItem(AGE_GATE_KEY);
        } catch (error) {
            console.error('Error clearing age gate verification:', error);
        }
    }
    
    /**
     * Get age gate verification details
     * @returns {Object|null} Verification details or null if not verified
     */
    static getAgeGateDetails() {
        try {
            const sessionData = sessionStorage.getItem(AGE_GATE_KEY);
            if (sessionData) {
                const data = JSON.parse(sessionData);
                return this.isVerificationValid(data) ? data : null;
            }
            
            const localData = localStorage.getItem(AGE_GATE_KEY);
            if (localData) {
                const data = JSON.parse(localData);
                return this.isVerificationValid(data) ? data : null;
            }
            
            return null;
        } catch (error) {
            console.error('Error getting age gate details:', error);
            return null;
        }
    }
    
    /**
     * Get days until age gate verification expires
     * @returns {number|null} Days until expiry, null if not verified or no expiry
     */
    static getDaysUntilExpiry() {
        const details = this.getAgeGateDetails();
        
        if (!details || !details.expiry) {
            return null;
        }
        
        const now = new Date();
        const expiry = new Date(details.expiry);
        const diffTime = expiry - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        return diffDays;
    }
    
    /**
     * Check if age gate verification is expiring soon (within 30 days)
     * @returns {boolean} True if expiring soon
     */
    static isExpiringSoon() {
        const daysUntilExpiry = this.getDaysUntilExpiry();
        return daysUntilExpiry !== null && daysUntilExpiry <= 30;
    }
    
    /**
     * Validate age from date of birth
     * @param {string} dateOfBirth - Date of birth in YYYY-MM-DD format
     * @returns {Object} Validation result
     */
    static validateAge(dateOfBirth) {
        try {
            const birthDate = new Date(dateOfBirth);
            const today = new Date();
            
            // Check if date is valid
            if (isNaN(birthDate.getTime()) || birthDate > today) {
                return {
                    isValid: false,
                    age: null,
                    error: 'Invalid date of birth'
                };
            }
            
            // Calculate age
            const age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            
            // Adjust age if birthday hasn't occurred this year
            const actualAge = (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) 
                ? age - 1 
                : age;
            
            return {
                isValid: actualAge >= 18,
                age: actualAge,
                error: actualAge < 18 ? 'You must be 18 years or older' : null
            };
            
        } catch (error) {
            return {
                isValid: false,
                age: null,
                error: 'Invalid date format'
            };
        }
    }
    
    /**
     * Create verification data object
     * @param {number} age - User's age
     * @returns {Object} Verification data
     */
    static createVerificationData(age) {
        return {
            verified: true,
            timestamp: new Date().toISOString(),
            expiry: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year
            age: age,
            version: '1.0' // For future compatibility
        };
    }
    
    /**
     * Force re-verification (for testing or policy changes)
     */
    static forceReVerification() {
        this.clearAgeGate();
    }
    
    /**
     * Check if age gate should be shown
     * @returns {boolean} True if age gate should be displayed
     */
    static shouldShowAgeGate() {
        return !this.isAgeGateVerified();
    }
    
    /**
     * Generate age gate token for API requests
     * @returns {string|null} Base64 encoded token or null if not verified
     */
    static getAgeGateToken() {
        const details = this.getAgeGateDetails();
        if (!details) {
            return null;
        }
        
        try {
            const tokenString = JSON.stringify(details);
            return btoa(tokenString); // Browser-compatible base64 encoding
        } catch (error) {
            console.error('Error generating age gate token:', error);
            return null;
        }
    }
}

export default AgeGateManager; 